<script setup>
import { DsButton } from '@dss360/design-system'
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useBreadcrumbStore } from '@/shared/stores/breadcrumb.store.js'

const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()
const breadcrumbItems = computed(() => breadcrumbStore.breadcrumbs)
const isVisible = computed(() => breadcrumbStore.displayBreadcrumb)
const actions = computed(() => breadcrumbStore.actions)

const separator = '>'

const pt = {
  root: {
    class: 'transition-opacity duration-200 py-2.5',
  },
  menu: {
    class: 'flex items-center gap-2 p-0',
  },
  item: {
    class: 'text-sm text-content-muted',
  },
  separatoricon: {
    class: 'size-3 text-content-muted',
  },
}

/**
 * Transforms a route identifier into a valid router-link prop
 * @param {string | object} route - Route identifier
 * @returns {object | undefined} Route object for router-link
 */
function getRouteObject(route) {
  if (!route)
    return

  try {
    if (typeof route === 'object' && route.name) {
      return route
    }

    if (typeof route === 'string') {
      return { name: route }
    }
  }
  catch (error) {
    console.error('Error processing breadcrumb route:', error)
  }
}

/**
 * Handles breadcrumb item click
 * @param {Event} event - Click event
 */
function onItemClick(event) {
  try {
    const { item } = event
    if (item.route) {
      const route = getRouteObject(item.route)
      if (route) {
        router.push(route)
      }
    }
  }
  catch (error) {
    console.error('Error navigating from breadcrumb:', error)
  }
}

function onBackClick() {
  if (breadcrumbItems.value && breadcrumbItems.value.length > 1) {
    const previousItem = breadcrumbItems.value[breadcrumbItems.value.length - 2]
    if (previousItem && previousItem.route) {
      const route = getRouteObject(previousItem.route)
      if (route) {
        router.push(route)
        return
      }
    }
  }

  router.back()
}

onMounted(() => {
  if (breadcrumbItems.value && breadcrumbItems.value.length > 0) {
    breadcrumbItems.value.forEach((item) => {
      if (!item.label) {
        console.warn('Breadcrumb item missing label:', item)
      }
    })
  }
})
</script>

<template>
  <div
    v-if="isVisible && breadcrumbItems && breadcrumbItems.length > 0"
    class="bg-white dark:bg-dss-surface-0 border-b border-dss-surface-200 flex items-center gap-3.5 px-5 py-1 w-full"
  >
    <ds-button
      v-if="breadcrumbItems.length > 1"
      icon="pi pi-arrow-left"
      :label="$t('common.back')"
      size="small"
      severity="secondary"
      outlined
      @click="onBackClick"
    />

    <Breadcrumb
      :model="breadcrumbItems"
      :separator="separator"
      :unstyled="true"
      :pt="pt"
      class="border-0 bg-transparent px-0"
      @item-click="onItemClick"
    >
      <template #item="{ item }">
        <component
          :is="item.route ? 'router-link' : 'div'"
          :to="getRouteObject(item.route)"
          class="flex items-center"
        >
          <span
            v-if="item.icon"
            :class="item.icon"
            class="mr-1"
          />
          <span
            class="p-breadcrumb-item-label"
            data-pc-section="itemlabel"
          >{{ item.translate === false ? item.label : $t(item.label) }}</span>
        </component>
      </template>
    </Breadcrumb>

    <div
      v-if="actions && actions.length > 0"
      class="ml-auto flex items-center gap-2"
    >
      <ds-button
        v-for="(action, index) in actions"
        :key="index"
        :label="action.label ? $t(action.label) : ''"
        :icon="action.icon"
        :severity="action.severity || 'secondary'"
        :outlined="action.outlined"
        :disabled="action.disabled"
        :loading="action.loading"
        size="small"
        @click="action.action && action.action()"
      />
    </div>
  </div>
</template>
